import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { apiClient } from "../utils/apiClient/index.ts";
import type { Session } from "@supabase/supabase-js";
import type { User } from "../types";

interface AuthContextType {
  currentUser: User | null; // the actual signed-in user
  impersonatedUser: User | null; // the user being impersonated, if any
  isSuperadmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
  startImpersonation: (targetUserId: string) => Promise<void>;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkSession = async (session: Session | null) => {
    if (!session) {
      setLoading(false);
      return;
    }
    if (!session.access_token) return;

    apiClient.setAuthToken(session.access_token);

    try {
      if (!currentUser) {
        const dbUser = await apiClient.getCurrentUser();
        setCurrentUser(dbUser);

        const result = await apiClient.checkSuperadmin();
        setIsSuperadmin(result.isSuperadmin);
      }
      setLoading(false);
    } catch (error: unknown) {
      console.error("Session validation failed:", error);
      await supabase.auth.signOut({ scope: "local" });
      setCurrentUser(null);
      setIsSuperadmin(false);
      apiClient.setAuthToken(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        await checkSession(session);
      } catch (e) {
        console.error("Error checking session:", e);
        setCurrentUser(null);
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      checkSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // --- Basic auth methods ---
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    setLoading(true); // Show loading screen during sign out
    const response = await supabase.auth.signOut({ scope: "global" });
    if (response.error) {
      await supabase.auth.signOut({ scope: "local" });
    }
    setCurrentUser(null);
    setImpersonatedUser(null);
    setIsSuperadmin(false);
    apiClient.setAuthToken(null);
    navigate("/login");
    setLoading(false);
  };

  const refreshCurrentUser = async () => {
    if (currentUser) {
      try {
        const dbUser = await apiClient.getCurrentUser();
        setCurrentUser(dbUser);
        if (impersonatedUser) {
          // refresh impersonated user as well
          const impUser = await apiClient.getUserById(impersonatedUser.id);
          setImpersonatedUser(impUser);
        }
      } catch (error) {
        console.error("Error refreshing user data:", error);
      }
    }
  };

  // --- Impersonation ---
  const startImpersonation = async (targetUserId: string) => {
    if (!isSuperadmin) throw new Error("Only superadmins can impersonate");

    const userToImpersonate = await apiClient.getUserById(targetUserId);
    if (!userToImpersonate) throw new Error("Target user not found");

    setImpersonatedUser(userToImpersonate);
  };

  const stopImpersonation = () => {
    setImpersonatedUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        impersonatedUser,
        isSuperadmin,
        loading,
        signIn,
        signUp,
        signOut,
        refreshCurrentUser,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
